import { STYLE_ALIASES, ANGLE_ALIASES, type AdStyle, type AdAngle, type AdFilter, normalizeAdFilter } from './constants/adStyles';
import type { CreativeAllocation } from './creativeAllocation';

export type AdKeyword = { axis: 'styles'; value: AdStyle; start: number; end: number }
  | { axis: 'angles'; value: AdAngle; start: number; end: number };
const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const aliases = [
  ...Object.entries(STYLE_ALIASES).map(([alias, value]) => ({alias, value, axis:'styles' as const})),
  ...Object.entries(ANGLE_ALIASES).map(([alias, value]) => ({alias, value, axis:'angles' as const})),
].sort((a,b) => b.alias.length - a.alias.length);

/** A rejected direction is context, never an automatically selected filter. */
export function isNegatedAdKeyword(text: string, start: number, end: number): boolean {
  const before = text.slice(0, start).toLowerCase().replace(/[’‘]/g, "'").split(/[.!?;,\n]|\bbut\b|\binstead use\b/).at(-1) ?? '';
  const after = text.slice(end).toLowerCase();
  return /\b(?:instead of|rather than|without|avoid|skip|exclude|excluding|no|not|never|don't|do not)\s+(?:(?:the|an?|any|that|this|use|using|want|include|choose|pick|previous|earlier|suggested)\s+){0,5}$/.test(before)
    || /^\s*(?:(?:style|angle|direction)\s+)?(?:is\s+)?(?:not wanted|isn't wanted|shouldn't be used|should not be used|is excluded)\b/.test(after);
}

/** Earliest complete keyword wins. Longer aliases and style labels break ties. */
export function firstAdKeyword(text: string): AdKeyword | null {
  // Attachment transport and URLs must never become invisible selections.
  const visible = text.replace(/\[(?:Attached|Reference) image(?: to clone)?:[^\]]+\]|https?:\/\/[^\s]+/gi, value => ' '.repeat(value.length));
  let first: AdKeyword | null = null;
  for (const {alias, value, axis} of aliases) {
    const pattern = escape(alias).replace(/\s+/g, '\\s+');
    for (const match of Array.from(visible.matchAll(new RegExp(`(?<![\\p{L}\\p{N}_])${pattern}(?![\\p{L}\\p{N}_])`, 'giu')))) {
      const end = match.index! + match[0].length;
      if (!isNegatedAdKeyword(visible, match.index!, end) && (!first || match.index! < first.start)) {
        first = {axis,value,start:match.index!,end} as AdKeyword;
      }
    }
  }
  return first;
}
export function createAdFilter(text: string): AdFilter {
  const keyword = firstAdKeyword(text);
  return {styles:keyword?.axis === 'styles' ? [keyword.value] : null,
    angles:keyword?.axis === 'angles' ? [keyword.value] : null};
}
/** Keep the first batch quantity rather than summing later keyword quotas. */
export function createAdCount(text: string, fallback = 20): number {
  if(/\b(?:make|create|generate|render|build|give\s+me|want|need)\s+(?:me\s+)?(?:a|an|single)\s+(?:[\w-]+\s+){0,3}(?:ad|image|creative)(?=\s|[.!?,;]|$)/i.test(text))return 1;
  const words:Record<string,number>={one:1,two:2,three:3,four:4,five:5,six:6,seven:7,eight:8,nine:9,ten:10,eleven:11,twelve:12,twenty:20,thirty:30,forty:40,fifty:50,hundred:100};
  const quantity=`(?:\\d{1,6}|${Object.keys(words).join('|')})`;
  const match = new RegExp(`\\b(?:make|create|generate|build|give\\s+me|want|need)\\s+(?:me\\s+)?(${quantity})\\b`,'i').exec(text)
    ?? new RegExp(`\\b(${quantity})\\s+(?:[\\p{L}& -]+\\s+)?(?:ads?|images?|creatives?)\\b`,'iu').exec(text);
  const count = match ? Number(match[1]) || words[match[1].toLowerCase()] : fallback;
  return count > 0 ? count : fallback;
}
export function templateAdPrompt(style: string, existing: string, axis?: 'style' | 'angle'): string {
  const keyword = axis === 'angle' && style.trim().toLowerCase() === 'testimonial' ? 'Testimonial angle' : style;
  return `Make me ${createAdCount(existing)} ${keyword} ads.`;
}
/** One batch only; subsequent plain-text keywords do not introduce quotas. */
export function createAdAllocations(text: string, count: number, filter: {styles?:string[] | null;angles?:string[] | null} = createAdFilter(text)): CreativeAllocation[] {
  const selected=normalizeAdFilter(filter.styles,filter.angles);
  return [{count,...(selected.styles?.[0] ? {style:selected.styles[0]} : selected.angles?.[0] ? {angle:selected.angles[0]} : {})}];
}

/** Mark the batch format explicitly so later plain keywords cannot change anatomy. */
export function createAdPlanningBrief(text:string, allocation:CreativeAllocation):string {
  return text+'\nOnly the first recognized ad angle or style is the batch selection. Keep that selection for every ad. Other keyword occurrences are ordinary text, not extra format or angle selections. Choose a varied mix on the other axis automatically from compatible winning ads.'
    +'\nREQUIRED BATCH FORMAT: '+(allocation.style ?? 'unspecified');
}
